import { memo, useCallback, useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

import avatarImg from '@/assets/images/avatar.jpg';
import { useBoundStore } from '@/store/store';

import { Bubble } from './components/bubble/Bubble';
import { MemberRegistration } from './components/registration/MemberRegistration';
import { NewMembership } from './components/registration/NewMembership';
import { NewMembershipConsent } from './components/registration/NewMembershipConsent';
import { NewMemberShipRecap } from './components/registration/NewMembershipRecap';
import { PauseMembership } from './components/registration/PauseMembership';
import { PaymentMembership } from './components/registration/PaymentMembership';
import { Route } from './components/route/Route';
import { useAuthVerification } from './hooks/useAuthVerification';
import { useEfitnessRefreshToken } from './hooks/useEfitnessRefreshToken';
import { hasPaymentParam } from './lib/utils';
import { Chat } from './routes/chat/Chat';
import { ChatWindow } from './routes/chat/ChatWindow';
import { GroupClassDetailRoute } from './routes/groupClasses/GroupClassDetailRoute';
import { GroupClassesRoute } from './routes/groupClasses/GroupClassesRoute';
import { InitialRoute } from './routes/initial/InitialRoute';
import { SignInRoute } from './routes/singIn/SignInRoute';
import { Threads } from './routes/threads/Threads';

export const Widget = memo(function Widget() {
  const { open, setUnread, pushRoute, user } = useBoundStore(
    useShallow(state => ({
      open: state.open,
      setUnread: state.setUnread,
      pushRoute: state.pushRoute,
      user: state.user,
    })),
  );

  /**
   * Handle authenticated users verification.
   */
  useAuthVerification();

  /**
   * Subscribe to handling eFitness refresh token.
   */
  useEfitnessRefreshToken();

  /**
   * Redirect to chat if user is authenticated,
   * otherwise redirect to initial route.
   */
  useEffect(() => {
    if (user) {
      pushRoute('chat');
    } else {
      pushRoute('initial');
    }
  }, [pushRoute, user]);

  const handleOpen = useCallback(() => {
    open();
    setUnread(0);
  }, [open, setUnread]);

  // We want to verify the chat user on mount
  useEffect(() => {
    if (hasPaymentParam()) {
      open();
      pushRoute('chat');
    }
  }, [open, pushRoute]);

  return (
    <>
      <ChatWindow>
        <Route name='initial'>
          <InitialRoute />
        </Route>
        <Route name='chat'>
          <Chat />
        </Route>
        <Route name='newMembership'>
          <NewMembership />
        </Route>
        <Route name='newMembershipConsent'>
          <NewMembershipConsent />
        </Route>
        <Route name='newMembershipRecap'>
          <NewMemberShipRecap />
        </Route>
        <Route name='membershipPayment'>
          <PaymentMembership />
        </Route>
        <Route name='memberRegistration'>
          <MemberRegistration />
        </Route>
        <Route name='signIn'>
          <SignInRoute />
        </Route>
        <Route name='threads'>
          <Threads />
        </Route>
        <Route name='pauseMembership'>
          <PauseMembership />
        </Route>
        <Route name='groupClasses'>
          <GroupClassesRoute />
        </Route>
        <Route name='groupClassDetail'>
          <GroupClassDetailRoute />
        </Route>
      </ChatWindow>

      <Bubble src={avatarImg} onClick={handleOpen} />
    </>
  );
});
